import { autorun, reaction, runInAction, when } from "mobx";
import { initComponentStore } from "@/utils/mobx";
import { diffLines } from "diff";
import { appStore } from "@/app_store";
import { SandboxStore, SandboxSessionStore } from "@/app_store/agent";
import { Tab, evictAndAddTab } from "@/components/toolkit/Tabs/store";

import { ContextMenuStore } from "@/components/toolkit/ContextMenu/store";
import { TOUCH_MEDIA_QUERY, isTouchDevice } from "@/utils/device";
import { LineDecoration, MonacoEditorStore } from "@/components/toolkit/MonacoEditor/store";
import { getLanguage } from "@/utils/language";

function computeLineDecorations(original: string, modified: string): LineDecoration[] {
    const hunks = diffLines(original, modified);
    const result: LineDecoration[] = [];
    let line = 1;
    let prevWasRemove = false;

    for (const hunk of hunks) {
        const count = hunk.count ?? 0;
        if (hunk.removed) {
            prevWasRemove = true;
        } else if (hunk.added) {
            if (count > 0) {
                result.push({
                    startLine: line,
                    endLine: line + count - 1,
                    type: prevWasRemove ? 'modified' : 'added',
                });
                line += count;
            }
            prevWasRemove = false;
        } else {
            line += count;
            prevWasRemove = false;
        }
    }

    return result;
}

const EMPTY_DECORATIONS: LineDecoration[] = [];

const LANDSCAPE_QUERY = "(min-aspect-ratio: 1)";

export class CodeSectionStore {
    get splitSizes(): number[] {
        const s = appStore.account.settings?.ui_split_sizes;
        return s && s.length > 0 ? s : [15, 85];
    }
    isMobile: boolean = isTouchDevice();
    isLandscape: boolean = window.matchMedia(LANDSCAPE_QUERY).matches;
    openTabs: Tab[] = [];
    activeTabPath: string | null = null;
    fileContents: Map<string, string> = new Map();
    savedFileContents: Map<string, string> = new Map();
    savingPaths: Set<string> = new Set();
    get mobileAccordionIndex() { return appStore.state.mobileAccordionIndex; }
    set mobileAccordionIndex(v: number) { appStore.state.mobileAccordionIndex = v; }
    editModes: Set<string> = new Set();
    contextMenuStore: ContextMenuStore = new ContextMenuStore();
    monacoEditorStore: MonacoEditorStore = new MonacoEditorStore();
    deleteConfirmPath: string | null = null;
    renamePath: string | null = null;
    renameValue: string = "";
    isRenaming: boolean = false;
    createFolderParentPath: string | null = null;
    createFolderName: string = "";
    isCreatingFolder: boolean = false;
    createFileParentPath: string | null = null;
    createFileName: string = "";
    isCreatingFile: boolean = false;
    changedLineRanges: Map<string, LineDecoration[]> = new Map();
    pendingScrollToLine: number | null = null;
    mdPreviewActive: boolean = false;
    pumlPreviewActive: boolean = false;
    showDecorations: boolean = true;
    wordWrap: boolean = false;
    collapseLevel: 0 | 1 = 0;

    private _tabsLoaded!: boolean;
    private _vaultSessionId: string | null = null;
    private _containerSessionId: string | null = null;
    private _initDisposer: (() => void) | null = null;

    constructor() {
        initComponentStore(this, { contextMenuStore: false, monacoEditorStore: false });
        this._tabsLoaded = false;

        this._initDisposer = when(
            () => !appStore.agent.isOperationPending,
            () => {
                void this._initContainerSession();
                void this._initVaultSession();
            },
        );

        // Init vault session when vault becomes available after initial load
        reaction(
            () => this._vaultSource(),
            (sandbox) => {
                if (sandbox && !this._vaultSessionId) void this._initVaultSession();
            }
        );

        const project = appStore.project.selectedProject;
        if (project) {
            const rawPaths = this.isMobile ? (project.open_tabs_mobile ?? []) : (project.open_tabs ?? []);
            const maxTabs = this.isMobile
                ? (appStore.account.settings?.max_open_tabs_mobile ?? 2)
                : (appStore.account.settings?.max_open_tabs_desktop ?? 10);
            const savedPaths = rawPaths.length > maxTabs ? rawPaths.slice(rawPaths.length - maxTabs) : rawPaths;
            const rawActive = this.isMobile ? (project.active_tab_path_mobile || null) : (project.active_tab_path || null);
            const savedActive = rawActive && savedPaths.includes(rawActive) ? rawActive : (savedPaths[savedPaths.length - 1] ?? null);
            runInAction(() => {
                this.openTabs = savedPaths.map(encoded => {
                    const isVault = encoded.startsWith("vault:");
                    const path = isVault ? encoded.slice(6) : encoded;
                    const filename = path.split("/").filter(Boolean).pop() ?? path;
                    return { path, label: isVault ? `vault:${filename}` : filename, ...(isVault ? { isVault: true as const } : {}) };
                });
                this.activeTabPath = savedActive?.startsWith("vault:") ? savedActive.slice(6) : (savedActive ?? null);
            });
            if (this.activeTabPath) {
                appStore.state.setActiveFilePath(this.activeTabPath);
            }
        }
        this._tabsLoaded = true;

        reaction(
            () => this.openTabs.map(t => t.path).join(",") + "|" + (this.activeTabPath ?? ""),
            () => {
                if (!this._tabsLoaded) return;
                appStore.project.updateOpenTabs(
                    this.openTabs.map(t => t.isVault ? `vault:${t.path}` : t.path),
                    this.activeTabPath
                        ? (this.openTabs.find(t => t.path === this.activeTabPath)?.isVault ? `vault:${this.activeTabPath}` : this.activeTabPath)
                        : null,
                    this.isMobile,
                );
            }
        );

        reaction(
            () => appStore.project.selectedProjectId,
            () => {
                this._tabsLoaded = false;
                const project = appStore.project.selectedProject;
                const rawPaths = project
                    ? (this.isMobile ? (project.open_tabs_mobile ?? []) : (project.open_tabs ?? []))
                    : [];
                const maxTabs = this.isMobile
                    ? (appStore.account.settings?.max_open_tabs_mobile ?? 2)
                    : (appStore.account.settings?.max_open_tabs_desktop ?? 10);
                const savedPaths = rawPaths.length > maxTabs ? rawPaths.slice(rawPaths.length - maxTabs) : rawPaths;
                const rawActive = project
                    ? (this.isMobile ? (project.active_tab_path_mobile || null) : (project.active_tab_path || null))
                    : null;
                const savedActive = rawActive && savedPaths.includes(rawActive) ? rawActive : (savedPaths[savedPaths.length - 1] ?? null);
                runInAction(() => {
                    this.fileContents = new Map();
                    this.savedFileContents = new Map();
                    this.openTabs = savedPaths.map(encoded => {
                        const isVault = encoded.startsWith("vault:");
                        const path = isVault ? encoded.slice(6) : encoded;
                        const filename = path.split("/").filter(Boolean).pop() ?? path;
                    return { path, label: isVault ? `vault:${filename}` : filename, ...(isVault ? { isVault: true as const } : {}) };
                    });
                    this.activeTabPath = savedActive?.startsWith("vault:") ? savedActive.slice(6) : (savedActive ?? null);
                });
                this._tabsLoaded = true;
                appStore.state.setActiveFilePath(this.activeTabPath);
                // Reinit sessions since both container and vault change with project.
                // Clearing _vaultSessionId allows the vault sandbox reaction below to
                // reinitialize when the new vault's sandbox becomes available.
                runInAction(() => {
                    this._containerSessionId = null;
                    this._vaultSessionId = null;
                });
                void this._initContainerSession();
                this._updateFileWatches();
            }
        );

        // React to global activeFilePath / pendingLine changes (history nav, ReviewSection, deep links, etc.)
        reaction(
            () => ({ path: appStore.state.activeFilePath, line: appStore.state.pendingLine }),
            ({ path, line }) => {
                if (!path) return;
                this._syncToPath(path);
                if (this.isMobile) this.mobileAccordionIndex = 1;
                if (line !== null) {
                    runInAction(() => { this.pendingScrollToLine = line; });
                }
            }
        );

        // Direct open-in-code request from AgentPane widgets — route through global state so history is tracked.
        reaction(
            () => appStore.state.openInCodeRequest,
            (req) => {
                if (req) {
                    appStore.state.setActiveFilePath(req.path);
                    if (this.isMobile) this.mobileAccordionIndex = 1;
                }
            }
        );

        // When the container session becomes available, register file watches
        reaction(
            () => this._containerSessionId != null ? this._containerSource()?.sessions[this._containerSessionId] : null,
            (session) => { if (session) this._updateFileWatches(); }
        );

        // When the vault session becomes available, register file watches
        reaction(
            () => this._vaultSessionId != null ? this._vaultSource()?.sessions[this._vaultSessionId] : null,
            (session) => { if (session) this._updateFileWatches(); }
        );

        // Sync both sessions' files → fileContents live
        autorun(() => {
            const vaultSession = this._vaultSessionId ? this._vaultSource()?.sessions[this._vaultSessionId] : null;
            const containerSession = this._containerSessionId ? this._containerSource()?.sessions[this._containerSessionId] : null;

            for (const tab of this.openTabs) {
                const session = tab.isVault ? vaultSession : containerSession;
                if (!session) continue;
                const content = session.files[tab.path];
                if (content !== undefined) {
                    this.fileContents.set(tab.path, content);
                    this.savedFileContents.set(tab.path, content);
                }
            }
        });

        window.matchMedia(TOUCH_MEDIA_QUERY).addEventListener("change", (e) => {
            runInAction(() => { this.isMobile = e.matches; });
        });

        window.matchMedia(LANDSCAPE_QUERY).addEventListener("change", (e) => {
            runInAction(() => { this.isLandscape = e.matches; });
        });

        // Reload diff decorations when the active file or its git status entry changes
        reaction(
            () => {
                const path = this.activeRelativePath;
                const entry = appStore.git.changes?.find(e => e.path === path);
                return `${path ?? ''}|${entry ? entry.flags : 'none'}`;
            },
            (sig) => {
                const path = this.activeRelativePath;
                if (!path) return;
                const inGit = appStore.git.changes?.some(e => e.path === path) ?? false;
                if (!inGit) {
                    runInAction(() => { this.changedLineRanges.delete(path); });
                    return;
                }
                void this.loadChangedLines(path);
            },
            { fireImmediately: true }
        );

    }

    get activeRelativePath(): string | null {
        if (!this.activeTabPath) return null;
        return this.activeTabPath;
    }

    get isMarkdown(): boolean {
        return this.activeTabPath ? getLanguage(this.activeTabPath) === "markdown" : false;
    }

    get isPuml(): boolean {
        return this.activeTabPath ? getLanguage(this.activeTabPath) === "puml" : false;
    }

    get previewVisible(): boolean {
        return this.isMarkdown && this.mdPreviewActive;
    }

    get pumlPreviewVisible(): boolean {
        return this.isPuml && this.pumlPreviewActive;
    }

    get pumlContent(): string {
        return (this.isPuml && this.activeTabPath) ? (this.fileContents.get(this.activeTabPath) ?? "") : "";
    }

    get activeTabLanguage(): string {
        return this.activeTabPath ? getLanguage(this.activeTabPath) : "plaintext";
    }

    get isActiveFileDeleted(): boolean {
        const path = this.activeRelativePath;
        if (!path) return false;
        const entry = appStore.git.changes?.find(e => e.path === path);
        if (!entry) return false;
        return entry.labels.some(l => l === "index_deleted" || l === "wt_deleted");
    }

    get activeTabDecorations(): LineDecoration[] {
        const path = this.activeRelativePath;
        if (!path) return EMPTY_DECORATIONS;
        return this.changedLineRanges.get(path) ?? EMPTY_DECORATIONS;
    }

    async loadChangedLines(relativePath: string) {
        try {
            const entry = appStore.git.changes?.find(e => e.path === relativePath);
            const isUntracked = entry?.labels.includes("wt_new") ?? false;
            const isStaged = entry?.labels.some(l => l.startsWith("index_")) ?? false;
            const a = isUntracked ? "" : "HEAD";
            const staged = isStaged && !isUntracked;
            console.log(`[CodeSection] loadChangedLines start`, { relativePath, a, staged, labels: entry?.labels });
            const diff = await appStore.git.getDiffFile(relativePath, { a, staged });
            console.log(`[CodeSection] loadChangedLines done`, {
                relativePath,
                originalLen: diff.original.length,
                modifiedLen: diff.modified.length,
                originalSnippet: diff.original.slice(0, 80),
            });
            const ranges = computeLineDecorations(diff.original, diff.modified);
            console.log(`[CodeSection] loadChangedLines ranges`, { relativePath, ranges });
            runInAction(() => { this.changedLineRanges.set(relativePath, ranges); });
        } catch (e) {
            console.error(`[CodeSection] loadChangedLines error`, { relativePath, error: e });
        }
    }

    get editorFontSize(): number {
        const settings = appStore.account.settings;
        return (this.isMobile ? settings?.editor_font_size_mobile : settings?.editor_font_size_desktop) ?? 13;
    }

    setEditorFontSize(size: number) {
        if (this.isMobile) {
            appStore.account.saveSettings({ editor_font_size_mobile: size });
        } else {
            appStore.account.saveSettings({ editor_font_size_desktop: size });
        }
    }

    get selectedProjectName() {
        return appStore.project.selectedProject?.name ?? null;
    }

    get selectedProjectRelativePath(): string {
        return appStore.project.selectedProject?.relative_path ?? "";
    }

    // FileBrowser's rootPath, in the same path space as session.container's sandbox: the
    // bare default Sandbox (no project selected) roots at MRGR_ROOT_FOLDER, so browsing a
    // project under it needs its /workspace-relative path -- but once a project is
    // actually selected, session.container becomes that project's own dedicated Sandbox
    // (PhysicalRepositoryWorkspace/ManagedRepositoryBranch/...worktree), already rooted AT
    // the project itself, so its paths start from "" instead. Reusing selectedProjectRelativePath
    // unconditionally here double-joins it onto that already-project-rooted sandbox's cwd.
    get fileBrowserRootPath(): string {
        if (appStore.state.vaultMode) return "";
        const container = appStore.agent.session.container;
        if (!container || container instanceof SandboxStore) return this.selectedProjectRelativePath;
        return "";
    }

    setMdPreviewActive(v: boolean) { this.mdPreviewActive = v; }
    setPumlPreviewActive(v: boolean) { this.pumlPreviewActive = v; }
    setShowDecorations(v: boolean) { this.showDecorations = v; }
    setWordWrap(v: boolean) { this.wordWrap = v; }
    toggleCollapseLevel() { this.collapseLevel = this.collapseLevel === 0 ? 1 : 0; }

    dispose() {
        this._initDisposer?.();

        const vaultSource = this._vaultSource();
        const vaultSessionId = this._vaultSessionId;
        this._vaultSessionId = null;
        if (vaultSource && vaultSessionId != null) vaultSource.destroy_session(vaultSessionId).catch(() => {});

        const containerSource = this._containerSource();
        const containerSessionId = this._containerSessionId;
        this._containerSessionId = null;
        if (containerSource && containerSessionId != null) containerSource.destroy_session(containerSessionId).catch(() => {});
    }

    private _vaultSource(): SandboxStore | null {
        return appStore.agent.session.project?.vault_sandbox ?? null;
    }

    private _containerSource(): SandboxStore | null {
        const container = appStore.agent.session.container;
        if (!container) return null;
        if (container instanceof SandboxStore) return container;
        return (container as any).sandbox ?? null;
    }

    private _sessionForPath(path: string): SandboxSessionStore | null {
        const tab = this.openTabs.find(t => t.path === path);
        if (tab?.isVault) {
            if (this._vaultSessionId == null) return null;
            return this._vaultSource()?.sessions[this._vaultSessionId] ?? null;
        }
        if (this._containerSessionId == null) return null;
        return this._containerSource()?.sessions[this._containerSessionId] ?? null;
    }

    private async _initVaultSession() {
        const source = this._vaultSource();
        if (!source) return;
        const created: { id: string } = await source.create_session();
        runInAction(() => { this._vaultSessionId = created.id; });
        this._updateFileWatches();
    }

    private async _initContainerSession() {
        const source = this._containerSource();
        if (!source) return;
        const created: { id: string } = await source.create_session();
        runInAction(() => { this._containerSessionId = created.id; });
        this._updateFileWatches();
    }

    private _updateFileWatches() {
        const vaultSession = this._vaultSessionId ? this._vaultSource()?.sessions[this._vaultSessionId] : null;
        const containerSession = this._containerSessionId ? this._containerSource()?.sessions[this._containerSessionId] : null;

        const vaultPaths = this.openTabs.filter(t => t.isVault).map(t => t.path);
        const containerPaths = this.openTabs.filter(t => !t.isVault).map(t => t.path);

        if (vaultSession) void vaultSession.watch_files(vaultPaths);
        if (containerSession) void containerSession.watch_files(containerPaths);
    }

    isFileDirty(path: string | null): boolean {
        if (!path) return false;
        const current = this.fileContents.get(path);
        const saved = this.savedFileContents.get(path);
        if (current === undefined || saved === undefined) return false;
        return current !== saved;
    }

    async saveFile(path: string) {
        if (this.savingPaths.has(path)) return;
        const content = this.fileContents.get(path);
        if (content === undefined) return;
        runInAction(() => { this.savingPaths.add(path); });
        try {
            const session = this._sessionForPath(path);
            if (session) await session.write_file(path, content);
            runInAction(() => {
                this.savedFileContents.set(path, content);
            });
        } catch {
            // ignore save errors silently
        } finally {
            runInAction(() => { this.savingPaths.delete(path); });
        }
    }

    // Backend-detected (engine-dependent) conflict regions for the active file, read
    // from the live-streamed `conflicts` model. Returns [] for non-git containers.
    getActiveConflicts(): any[] {
        const path = this.activeTabPath;
        if (!path) return [];
        const ws: any = (appStore.agent.session.container as any)?.workspace;
        const fileConflicts = ws?.conflicts?.find((c: any) => c.path === path);
        return fileConflicts?.regions ?? [];
    }

    // Git merge (or stash re-apply) in progress.
    get isMerging(): boolean {
        const ws: any = (appStore.agent.session.container as any)?.workspace;
        return ws?.pending_merge ?? false;
    }

    // Re-applying the pre-pull stash conflicted (2nd merge phase); only physical repos stash.
    get stashConflict(): boolean {
        const ws: any = (appStore.agent.session.container as any)?.workspace;
        return ws?.stash_conflict ?? false;
    }

    // Persist the active file after a CodeLens resolution edited its buffer.
    saveActive() {
        if (this.activeTabPath) this.saveFile(this.activeTabPath);
    }

    private _syncToPath(path: string) {
        if (path === this.activeTabPath) return;
        runInAction(() => {
            this._addTab(path);
            this.activeTabPath = path;
        });
        this._updateFileWatches();
    }

    private _addTab(path: string) {
        const maxTabs = this.isMobile
            ? (appStore.account.settings?.max_open_tabs_mobile ?? 2)
            : (appStore.account.settings?.max_open_tabs_desktop ?? 10);
        this.openTabs = evictAndAddTab(this.openTabs, path, maxTabs, appStore.state.vaultMode);
    }

    openFile(path: string) {
        appStore.state.setActiveFilePath(path);
        if (this.isMobile) {
            this.mobileAccordionIndex = 1;
        }
    }

    async goToDefinition(symbol: string, line: number, col: number, currentFile: string) {
        // TODO(app-model): "find_definition" no longer has a backend handler (old
        // agent.api.handlers.files was removed); no equivalent exists yet in the app model.
    }

    clearScrollToLine() {
        this.pendingScrollToLine = null;
    }

    setActiveTab(path: string) {
        appStore.state.setActiveFilePath(path);
    }

    navigateTab(direction: -1 | 1) {
        if (this.openTabs.length === 0) return;
        const index = this.openTabs.findIndex(t => t.path === this.activeTabPath);
        const next = (index + direction + this.openTabs.length) % this.openTabs.length;
        this.setActiveTab(this.openTabs[next].path);
    }

    closeTab(path: string) {
        const index = this.openTabs.findIndex(t => t.path === path);
        if (index === -1) return;
        const next = this.openTabs.filter(t => t.path !== path);
        this.openTabs = next;
        if (this.activeTabPath === path) {
            const newPath = next[index]?.path ?? next[index - 1]?.path ?? null;
            this.activeTabPath = newPath;
            // Route through state so the history monitor creates a browser history entry
            // and the back button can return to the closed tab's file.
            appStore.state.setActiveFilePath(
                newPath
            );
        }
        if (next.length === 0 && this.isMobile) {
            this.mobileAccordionIndex = 0;
        }
        this._updateFileWatches();
    }

    reorderTabs(fromIndex: number, toIndex: number) {
        const tabs = [...this.openTabs];
        const [tab] = tabs.splice(fromIndex, 1);
        tabs.splice(toIndex, 0, tab);
        this.openTabs = tabs;
    }

    moveActiveTab(direction: -1 | 1) {
        if (!this.activeTabPath) return;
        const index = this.openTabs.findIndex(t => t.path === this.activeTabPath);
        if (index === -1) return;
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= this.openTabs.length) return;
        this.reorderTabs(index, newIndex);
    }

    closeAllTabs() {
        this.openTabs = [];
        this.activeTabPath = null;
        if (this.isMobile) {
            this.mobileAccordionIndex = 0;
        }
        this._updateFileWatches();
    }

    closeAllTabsExceptActive() {
        if (!this.activeTabPath) return;
        this.openTabs = this.openTabs.filter(t => t.path === this.activeTabPath);
        this._updateFileWatches();
    }

    get code() {
        return appStore.project.selectedProject?.code ?? "";
    }

    setCode(code: string) {
        appStore.project.updateCode(code);
    }

    openRenameDialog(path: string) {
        this.renamePath = path;
        this.renameValue = path.split("/").filter(Boolean).pop() ?? "";
    }

    setRenameValue(value: string) {
        this.renameValue = value;
    }

    cancelRename() {
        this.renamePath = null;
        this.renameValue = "";
    }

    cancelDelete() {
        this.deleteConfirmPath = null;
    }

    async deleteFile() {
        const path = this.deleteConfirmPath;
        if (!path) return;
        try {
            // TODO(app-model): "delete_file" no longer has a backend handler (old
            // agent.api.handlers.files was removed); no equivalent exists yet in the app model.
            this.closeTab(path);
        } catch {
            // ignore
        }
        this.deleteConfirmPath = null;
    }

    async submitRename() {
        const path = this.renamePath;
        const newName = this.renameValue.trim();
        if (!path || !newName) return;
        this.isRenaming = true;
        try {
            // TODO(app-model): "rename_file" no longer has a backend handler (old
            // agent.api.handlers.files was removed); no equivalent exists yet in the app model.
            const parent = path.split("/").slice(0, -1).join("/");
            const newPath = parent + "/" + newName;
            this.openTabs = this.openTabs.map(t =>
                t.path === path ? { ...t, path: newPath, label: newName } : t
            );
            if (this.activeTabPath === path) this.activeTabPath = newPath;
            const content = this.fileContents.get(path);
            if (content !== undefined) {
                this.fileContents.set(newPath, content);
                this.fileContents.delete(path);
            }
        } catch {
            // ignore
        } finally {
            this.isRenaming = false;
            this.renamePath = null;
            this.renameValue = "";
        }
    }

    openCreateFolderDialog(parentPath: string) {
        this.createFolderParentPath = parentPath;
        this.createFolderName = "";
    }

    setCreateFolderName(value: string) {
        this.createFolderName = value;
    }

    cancelCreateFolder() {
        this.createFolderParentPath = null;
        this.createFolderName = "";
    }

    async submitCreateFolder() {
        const parent = this.createFolderParentPath;
        const name = this.createFolderName.trim();
        if (!parent || !name) return;
        this.isCreatingFolder = true;
        try {
            // TODO(app-model): "create_folder" no longer has a backend handler (old
            // agent.api.handlers.files was removed); no equivalent exists yet in the app model.
        } catch {
            // ignore
        } finally {
            this.isCreatingFolder = false;
            this.createFolderParentPath = null;
            this.createFolderName = "";
        }
    }

    openCreateFileDialog(parentPath: string) {
        this.createFileParentPath = parentPath;
        this.createFileName = "";
    }

    setCreateFileName(value: string) {
        this.createFileName = value;
    }

    cancelCreateFile() {
        this.createFileParentPath = null;
        this.createFileName = "";
    }

    async submitCreateFile() {
        const parent = this.createFileParentPath;
        const name = this.createFileName.trim();
        if (parent === null || !name) return;
        this.isCreatingFile = true;
        try {
            const path = parent ? `${parent}/${name}` : name;
            const isVault = appStore.state.vaultMode;
            const session = isVault
                ? (this._vaultSessionId ? this._vaultSource()?.sessions[this._vaultSessionId] : null)
                : (this._containerSessionId ? this._containerSource()?.sessions[this._containerSessionId] : null);
            if (session) await session.write_file(path, "");
            this.openFile(path);
        } catch {
            // ignore
        } finally {
            this.isCreatingFile = false;
            this.createFileParentPath = null;
            this.createFileName = "";
        }
    }

    setFileContent(path: string, content: string) {
        this.fileContents.set(path, content);
    }

    isInEditMode(path: string | null): boolean {
        if (!this.isMobile) return true;
        if (!path) return false;
        return this.editModes.has(path);
    }

    toggleEditMode(path: string | null) {
        if (!path) return;
        if (this.editModes.has(path)) {
            this.editModes.delete(path);
        } else {
            this.editModes.add(path);
        }
    }

    setSplitSizes(sizes: number[]) {
        appStore.account.saveSettings({ ui_split_sizes: sizes });
    }

}
